import { differenceInMinutes } from 'date-fns'
import utcToZonedTime from 'date-fns-tz/utcToZonedTime'
import format from 'date-fns/format'
import isFuture from 'date-fns/isFuture'
import isPast from 'date-fns/isPast'
import isToday from 'date-fns/isToday'
import { GetterTree } from 'vuex'

import { RootState } from '@/store/types'

import { Booking, BookingsListByMonth, BookingsState } from './types'

type SortFn = (a: Booking, b: Booking) => 1 | -1 | 0

const sortByDateAsc: SortFn = (a: Booking, b: Booking) => {
  const dateA = new Date(a.schedule.from_date_time)
  const dateB = new Date(b.schedule.from_date_time)

  if (dateA < dateB) return -1
  if (dateA > dateB) return 1
  return 0
}

const sortByDateDesc: SortFn = (a: Booking, b: Booking) => {
  const dateA = new Date(a.schedule.from_date_time)
  const dateB = new Date(b.schedule.from_date_time)

  if (dateA > dateB) return -1
  if (dateA < dateB) return 1
  return 0
}

const mapBookingsListByMonth = (
  bookings: Booking[],
  sortFn: SortFn,
): BookingsListByMonth =>
  [...bookings]
    .sort(sortFn)
    .reduce(
      (
        carry: BookingsListByMonth,
        {
          booking_id: bookingId,
          caregiver: {
            name: { last_name, first_name },
          },
          is_first_booking: isFirstBooking,
          schedule: { from_date_time, to_date_time },
          state: status,
          pricing,
        }: Booking,
      ): BookingsListByMonth => {
        const fromDateTime = utcToZonedTime(from_date_time, 'Europe/Berlin')
        const toDateTime = utcToZonedTime(to_date_time, 'Europe/Berlin')
        const yearMonthKey = `${format(fromDateTime, 'yyyy')}-${format(
          fromDateTime,
          'MM',
        )}`
        const grossPriceInCents =
          pricing.gross_price_per_hour_in_cents *
          (differenceInMinutes(
            new Date(to_date_time),
            new Date(from_date_time),
          ) /
            60)

        return {
          ...carry,
          [yearMonthKey]: [
            ...(carry[yearMonthKey] || []),
            {
              bookingId,
              caregiverName: `${first_name} ${last_name}`,
              grossPriceInCents,
              fromDateTime,
              isFirstBooking,
              isFuture: isFuture(fromDateTime),
              isPast: isPast(toDateTime),
              isToday: isToday(fromDateTime),
              status,
              toDateTime,
            },
          ],
        }
      },
      {},
    )

const bookingsGetters: GetterTree<BookingsState, RootState> = {
  upcomingBookingsListByMonth({ upcomingBookings: bookings }) {
    return mapBookingsListByMonth(bookings, sortByDateAsc)
  },

  pastBookingsListByMonth({ pastBookings: bookings }) {
    return mapBookingsListByMonth(bookings, sortByDateDesc)
  },

  noUpcomingBookings({ noUpcomingBookings }) {
    return noUpcomingBookings
  },

  noPastBookings({ noPastBookings }) {
    return noPastBookings
  },
}

export default bookingsGetters
